import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Progress } from '@oca/ui';

import { errorMessage } from '../lib/format';

/** White panel that groups a table, a form or a chart. */
export const Card = styled.section`
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  min-width: 0;
`;

export const CardHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
`;

export const CardTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #181d27;
`;

export const CardBody = styled.div`
  padding: 20px;
`;

export const SectionTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 16px;
  font-weight: 600;
  color: #181d27;
`;

export const Muted = styled.span`
  font-size: 12px;
  color: #64748b;
`;

export function EmptyState({ title, children }) {
  return (
    <EmptyBox>
      <EmptyTitle>{title}</EmptyTitle>
      {children && <EmptyHint>{children}</EmptyHint>}
    </EmptyBox>
  );
}

EmptyState.propTypes = {
  title: PropTypes.node.isRequired,
  // eslint-disable-next-line react/require-default-props
  children: PropTypes.node,
};

export function LoadingBlock() {
  return (
    <EmptyBox as="div" aria-busy="true">
      <Progress />
    </EmptyBox>
  );
}

export function ErrorBanner({ error }) {
  if (!error) return null;
  return <ErrorBox role="alert">{errorMessage(error)}</ErrorBox>;
}

ErrorBanner.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types, react/require-default-props
  error: PropTypes.any,
};

const badgeColors = {
  uploaded: ['#eff6ff', '#1d4ed8'],
  processing: ['#fffbeb', '#b45309'],
  processed: ['#f0fdf4', '#15803d'],
  reviewed: ['#f0fdf4', '#15803d'],
  archived: ['#f1f5f9', '#475569'],
  expense: ['#fef2f2', '#b91c1c'],
  income: ['#f0fdf4', '#15803d'],
  transfer: ['#f5f3ff', '#6d28d9'],
};

/** Document status or transaction type, coloured by value. */
export function StatusBadge({ value }) {
  const [bg, color] = badgeColors[value] || ['#f1f5f9', '#475569'];
  return <Badge style={{ backgroundColor: bg, color }}>{value || '-'}</Badge>;
}

StatusBadge.propTypes = {
  // eslint-disable-next-line react/require-default-props
  value: PropTypes.string,
};

const EmptyBox = styled.div`
  padding: 48px 16px;
  text-align: center;
`;

const EmptyTitle = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: #475569;
  margin-bottom: 6px;
`;

const EmptyHint = styled.div`
  font-size: 13px;
  color: #94a3b8;
`;

const ErrorBox = styled.div`
  margin: 12px 0;
  padding: 10px 14px;
  border: 1px solid #ffa39e;
  border-radius: 8px;
  background: #fff1f0;
  color: #cf1322;
  font-size: 13px;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
  white-space: nowrap;
`;
